export interface RequestPolicy {
  /** How long to wait for the response after sending. */
  timeoutMs: number;
  /**
   * Send once more after a timeout (the worker may have been stopped). Only
   * for requests that are safe to repeat - never for writes.
   */
  retryAfterTimeout: boolean;
  /**
   * Rejects with `ChannelRequestNotAcknowledgedError` when nobody acknowledged
   * the request within this time (nobody serves the contract), instead of
   * waiting for the timeout.
   */
  acknowledgementTimeoutMs?: number;
  /**
   * The caller goes on after the timeout (e.g. signing out while the worker
   * still flushes): the request still rejects, but is not reported as a
   * worker that did not respond.
   */
  timeoutIsExpected?: boolean;
}

/** A context that receives a request acknowledges it at once; this allows for a busy main thread. */
export const ACKNOWLEDGEMENT_TIMEOUT_MS = 5_000;

/** Pause before the retry, so a worker that is starting up can settle. */
export const RETRY_BACKOFF_MS = 500;

/** Every request to the service worker uses one of these. */
export const RequestPolicies = {
  /** A single entity or a small list (children, breadcrumbs, preflight). */
  read: { timeoutMs: 10_000, retryAfterTimeout: true },
  /**
   * Data the worker reads from the API on request (not stored locally): the
   * server's time counts, and a slow answer is not a stopped worker, so a
   * repeat would only add load to a request that is already expensive.
   */
  serverRead: { timeoutMs: 30_000, retryAfterTimeout: false },
  /** Whole collections, filtering and search: may wait for a download. */
  bulkRead: { timeoutMs: 30_000, retryAfterTimeout: true },
  /** Create, edit, remove: never repeated, but always bounded. */
  mutation: { timeoutMs: 15_000, retryAfterTimeout: false },
  /** Reports and other server-side work. */
  longRunning: { timeoutMs: 120_000, retryAfterTimeout: false },
  /**
   * The first download of the project's data: as long as it progresses
   * (the page checks it with its own requests), a large project on a slow
   * network may take minutes.
   */
  projectDownload: { timeoutMs: 600_000, retryAfterTimeout: false },
  /**
   * Requests the page answers to the worker (e.g. a token). Never resent: the
   * retry exists to wake a stopped worker, which does not apply to a page.
   */
  pageAnswer: {
    timeoutMs: 30_000,
    retryAfterTimeout: false,
    acknowledgementTimeoutMs: ACKNOWLEDGEMENT_TIMEOUT_MS,
  },
} as const satisfies Record<string, RequestPolicy>;
